import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { LoginModal } from '../components/auth/LoginModal';
import { RegisterModal } from '../components/auth/RegisterModal';
import { EmailVerificationModal } from '../components/auth/EmailVerificationModal';
import { TwoFactorModal } from '../components/auth/TwoFactorModal';
import { PasswordResetModal } from '../components/auth/PasswordResetModal';
import { Button } from '../components/ui/Button';
import { useAuthStore } from '../store/authStore';
import {
  DocumentTextIcon,
  DocumentMagnifyingGlassIcon,
  ChatBubbleLeftRightIcon,
  DocumentDuplicateIcon,
  ClipboardDocumentListIcon,
  PencilSquareIcon,
  BookOpenIcon,
  IdentificationIcon,
  ClockIcon,
  ScaleIcon,
  HomeIcon,
  BriefcaseIcon,
  ShieldCheckIcon,
  BoltIcon,
  CloudArrowUpIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';

// Unsplash royalty-free images for legal theme
const IMAGES = {
  hero: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1920&q=80', // Lady Justice statue - classic legal imagery
  documents: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80', // Person signing document
  workflow: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=800&q=80', // Professional in suit
  courthouse: 'https://images.unsplash.com/photo-1564596823821-79b97151055e?w=800&q=80', // Courthouse columns
  office: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80', // Modern office
  contract: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80', // Legal AI - scales, AI icons, laptop
};

export const LandingPage: React.FC = () => {
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Note: Light theme for landing page is enforced by ThemeManager in App.tsx

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleRegistrationSuccess = (email: string) => {
    setShowRegister(false);
    setRegisteredEmail(email);
    setShowEmailVerification(true);
  };

  const handleEmailVerificationSuccess = () => {
    setShowEmailVerification(false);
    setShowLogin(true);
  };

  const handle2FASuccess = () => {
    setShow2FA(false);
    // User will be redirected by auth store
  };

  const handlePasswordResetSuccess = () => {
    setShowPasswordReset(false);
    setShowLogin(true);
  };

  // All AuxilioLegal features organized by category
  const documentFeatures = [
    {
      icon: DocumentMagnifyingGlassIcon,
      title: 'Document Review & Analysis',
      description:
        'Upload and analyze contracts, agreements, and legal documents with AI-powered insights. Get clause-by-clause breakdowns and identify potential risks.',
    },
    {
      icon: ChatBubbleLeftRightIcon,
      title: 'Legal Questions Answered',
      description:
        'Ask any legal question and get instant, AI-powered answers backed by comprehensive legal knowledge. Perfect for quick research.',
    },
    {
      icon: DocumentDuplicateIcon,
      title: 'Will Generation',
      description:
        'Create legally-sound wills tailored to your jurisdiction. Our AI guides you through the process with all required elements.',
    },
    {
      icon: ClipboardDocumentListIcon,
      title: 'Bill of Sale Creation',
      description:
        'Generate professional bills of sale for vehicles and property transactions. Automatically formatted for your jurisdiction.',
    },
    {
      icon: PencilSquareIcon,
      title: 'Legal Letter Generator',
      description:
        'Draft professional legal correspondence including demand letters, cease and desist notices, and formal complaints.',
    },
    {
      icon: BookOpenIcon,
      title: 'Legal Glossary',
      description:
        'Searchable database of legal terms with AI-powered explanations. Understand complex legal language in plain English.',
    },
  ];

  const workflowFeatures = [
    {
      icon: IdentificationIcon,
      title: 'Name Change Package',
      description:
        'Complete name change document package with court petitions, affidavits, and step-by-step guidance for your jurisdiction.',
    },
    {
      icon: ClockIcon,
      title: 'Legal Deadline Calculator',
      description:
        'Calculate filing deadlines, statute of limitations, and response periods. Export to calendar with automatic reminders.',
    },
    {
      icon: ScaleIcon,
      title: 'Small Claims Court Assistant',
      description:
        'Navigate small claims court with demand letters, complaint forms, and jurisdiction-specific guidance for all 50 states.',
    },
    {
      icon: HomeIcon,
      title: 'Tenant Rights Checker',
      description:
        'Analyze your lease agreement, check tenant rights by jurisdiction, and generate response letters for landlord disputes.',
    },
    {
      icon: BriefcaseIcon,
      title: 'Employment Document Advisor',
      description:
        'Analyze employment contracts, offer letters, and termination notices. Generate workplace complaint letters for discrimination, wage theft, and more.',
    },
  ];

  const benefits = [
    {
      icon: BoltIcon,
      title: 'Lightning Fast',
      description: 'Get instant answers and analysis powered by advanced AI technology.',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Secure & Private',
      description: 'Your documents and data are protected with enterprise-grade security.',
    },
    {
      icon: CloudArrowUpIcon,
      title: 'Easy Upload',
      description: 'Support for PDF, DOCX, RTF, TXT, spreadsheets, and PowerPoint files.',
    },
    {
      icon: GlobeAltIcon,
      title: '5 Languages',
      description: 'Full support for English, Spanish, French, German, and Italian.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gray-200/90 backdrop-blur-md border-b border-gray-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <div className="flex items-center">
              <img
                src="/logo_light.png"
                alt="Auxilio"
                className="h-12 dark:hidden"
              />
              <img
                src="/logo_dark.png"
                alt="Auxilio"
                className="h-12 hidden dark:block"
              />
            </div>

            {/* Auth Buttons */}
            <div className="flex space-x-3">
              <Button variant="secondary" onClick={() => setShowLogin(true)}>
                Login
              </Button>
              <Button onClick={() => setShowRegister(true)}>Register</Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section with Background Image */}
      <section className="relative pt-20 pb-20 px-4 sm:px-6 lg:px-8 min-h-[90vh] flex items-center">
        {/* Background Image */}
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${IMAGES.hero})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/85 to-gray-900/70 dark:from-black/95 dark:via-black/85 dark:to-black/70" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-left">
              {/* Title */}
              <div className="mb-6 flex items-center">
                <img
                  src="/auxilio_word_both.png"
                  alt="Auxilio"
                  className="h-[30px] sm:h-[42px] lg:h-12 drop-shadow-lg self-center"
                />
                <img
                  src="/scales-logo.png"
                  alt="Scales"
                  className="h-12 sm:h-16 lg:h-20 w-12 sm:w-16 lg:w-20 mx-2 sm:mx-3 drop-shadow-lg self-center"
                />
                <img
                  src="/legal_word_both.png"
                  alt="Legal"
                  className="h-[38px] sm:h-[53px] lg:h-[61px] drop-shadow-lg self-center -mb-[5px] sm:-mb-[7px] lg:-mb-[8px]"
                />
              </div>

              <p className="text-xl sm:text-2xl text-gray-200 mb-6 max-w-xl">
                Your AI-Powered Legal Assistant for Everyday Legal Needs
              </p>

              <p className="text-lg text-gray-300 mb-8 max-w-lg">
                11 powerful legal tools to help you understand documents, generate paperwork,
                calculate deadlines, and navigate common legal situations with confidence.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                <Button size="lg" onClick={() => setShowRegister(true)} className="text-lg px-8 py-4">
                  Get Started Free
                </Button>
                <Button
                  size="lg"
                  variant="secondary"
                  onClick={() => setShowLogin(true)}
                  className="text-lg px-8 py-4 bg-white/10 border-white/30 text-white hover:bg-white/20"
                >
                  Login to Your Account
                </Button>
              </div>
            </div>

            {/* Hero Image Card */}
            <div className="hidden lg:block">
              <div className="relative">
                <img
                  src="/legal-ai-feature.jpg"
                  alt="AI-powered legal assistance"
                  className="rounded-2xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-blue-600 text-white p-4 rounded-xl shadow-lg">
                  <p className="text-3xl font-bold">11+</p>
                  <p className="text-sm">Legal Tools</p>
                </div>
                <div className="absolute -top-6 -right-6 bg-green-600 text-white p-4 rounded-xl shadow-lg">
                  <p className="text-3xl font-bold">5</p>
                  <p className="text-sm">Languages</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Document Tools Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            {/* Image */}
            <div className="order-2 lg:order-1">
              <img
                src={IMAGES.documents}
                alt="Document analysis"
                className="rounded-2xl shadow-xl"
              />
            </div>

            {/* Content */}
            <div className="order-1 lg:order-2">
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-full mr-4">
                  <DocumentTextIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                  Document Analysis & Generation
                </h2>
              </div>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">
                AI-powered tools to review, understand, and create legal documents with confidence.
                Upload any document and get instant insights.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {documentFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-700 dark:to-gray-800 p-6 rounded-xl shadow-md hover:shadow-lg transition-all hover:-translate-y-1 border border-gray-200 dark:border-gray-600"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-lg">
                      <feature.icon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow Tools Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background Image with Overlay */}
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${IMAGES.courthouse})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
          }}
        >
          <div className="absolute inset-0 bg-gray-100/95 dark:bg-gray-900/95" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            {/* Content */}
            <div>
              <div className="flex items-center mb-4">
                <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-full mr-4">
                  <ScaleIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                  Guided Legal Workflows
                </h2>
              </div>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">
                Step-by-step assistance for common legal situations. Navigate complex processes
                with confidence using our guided workflows.
              </p>
            </div>

            {/* Image */}
            <div>
              <img
                src={IMAGES.workflow}
                alt="Professional assistance"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {workflowFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md hover:shadow-lg transition-all hover:-translate-y-1 border border-gray-200 dark:border-gray-600"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="bg-green-100 dark:bg-green-900 p-3 rounded-lg">
                      <feature.icon className="h-6 w-6 text-green-600 dark:text-green-400" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Why Choose AuxilioLegal?
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Built for speed, security, and ease of use
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="text-center p-6 bg-gradient-to-br from-gray-50 to-white dark:from-gray-700 dark:to-gray-800 rounded-xl shadow-md hover:shadow-lg transition-all hover:-translate-y-1"
              >
                <div className="flex justify-center mb-4">
                  <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-full">
                    <benefit.icon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Highlights with Image */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                Multi-Jurisdiction Support
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">
                AuxilioLegal understands that legal requirements vary by location. Our tools automatically
                adapt to your jurisdiction, providing relevant laws, forms, and guidance for:
              </p>
              <ul className="space-y-3 text-gray-600 dark:text-gray-300">
                <li className="flex items-center">
                  <span className="bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 rounded-full p-1 mr-3">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                  United States (all 50 states + DC)
                </li>
                <li className="flex items-center">
                  <span className="bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 rounded-full p-1 mr-3">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                  Canada (all provinces and territories)
                </li>
                <li className="flex items-center">
                  <span className="bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 rounded-full p-1 mr-3">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                  United Kingdom
                </li>
                <li className="flex items-center">
                  <span className="bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 rounded-full p-1 mr-3">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                  Australia (all states and territories)
                </li>
              </ul>
            </div>
            <div className="relative">
              <img
                src={IMAGES.office}
                alt="Modern legal office"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-600/80 to-transparent rounded-2xl flex items-end">
                <div className="p-8 text-white">
                  <h3 className="text-2xl font-bold mb-2">Available in 5 Languages</h3>
                  <p className="text-blue-100">
                    English, Spanish, French, German, and Italian
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-600 to-blue-800 dark:from-blue-700 dark:to-blue-900 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full translate-x-1/3 translate-y-1/3" />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <img
              src="/scales-logo.png"
              alt="Get Started"
              className="h-20 w-20 brightness-0 invert opacity-90"
            />
          </div>
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join thousands of users who trust AuxilioLegal for their legal document needs.
            Free to register, with no credit card required.
          </p>
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 justify-center">
            <Button
              size="lg"
              onClick={() => setShowRegister(true)}
              className="bg-white text-blue-700 hover:bg-gray-50 text-lg px-8 py-4 font-semibold shadow-lg"
            >
              Create Free Account
            </Button>
            <Button
              size="lg"
              variant="secondary"
              onClick={() => setShowLogin(true)}
              className="bg-blue-700 text-white hover:bg-blue-600 border-white text-lg px-8 py-4"
            >
              Sign In
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-gray-900 dark:bg-black">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex justify-center items-center space-x-2 mb-4">
            <img src="/scales-logo.png" alt="AuxilioLegal" className="h-8 w-8 brightness-0 invert" />
            <span className="text-xl font-bold text-white">AuxilioLegal</span>
          </div>
          <p className="text-gray-400 text-sm">
            AI-Powered Legal Assistant for Everyday Legal Needs
          </p>
          <p className="text-gray-500 text-xs mt-4 max-w-2xl mx-auto">
            AuxilioLegal provides legal information and document generation tools for educational purposes.
            It does not constitute legal advice. Consult a licensed attorney for specific legal matters.
          </p>
          <p className="text-gray-600 text-xs mt-4">
            Images provided by <a href="https://unsplash.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">Unsplash</a>
          </p>
          <p className="text-gray-500 text-xs mt-2">
            &copy; {new Date().getFullYear()} AuxilioLegal. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Modals */}
      <LoginModal
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
        onSwitchToRegister={() => {
          setShowLogin(false);
          setShowRegister(true);
        }}
        onSwitchToPasswordReset={() => {
          setShowLogin(false);
          setShowPasswordReset(true);
        }}
        onRequire2FA={() => {
          setShowLogin(false);
          setShow2FA(true);
        }}
      />

      <RegisterModal
        isOpen={showRegister}
        onClose={() => setShowRegister(false)}
        onSuccess={handleRegistrationSuccess}
      />

      <EmailVerificationModal
        isOpen={showEmailVerification}
        onClose={() => setShowEmailVerification(false)}
        onSuccess={handleEmailVerificationSuccess}
        email={registeredEmail}
      />

      <TwoFactorModal
        isOpen={show2FA}
        onClose={() => setShow2FA(false)}
        onSuccess={handle2FASuccess}
        onBack={() => {
          setShow2FA(false);
          setShowLogin(true);
        }}
      />

      <PasswordResetModal
        isOpen={showPasswordReset}
        onClose={() => setShowPasswordReset(false)}
        onSuccess={handlePasswordResetSuccess}
        onSwitchToLogin={() => {
          setShowPasswordReset(false);
          setShowLogin(true);
        }}
      />
    </div>
  );
};
